import React, { useMemo } from "react";

import logger from "../../../../../utils/LoggerFrontend";
import { ButtonOutlinedAnchor } from "../../Components/Buttons.jsx";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import CampaignCreatePageSelectorClient from "../../PageSelectors/Campaign__Create_PageSelector__Client.jsx";
import CampaignCreatePerformanceEditor from "../../campaign-create-performance-editor/CampaignCreatePerformanceEditor.jsx";
import ChannelDataEditor from "../../Campaign__Create_ChannelDataEditor.jsx";
import { Tooltip } from "../../Components";
import { channelsByShortName } from "../../../../../data/app__channels";

import * as VerticalSC from "./../post-editor-vertical/PostEditorVertical.styled";

import {
  PostEditorFacebookWrapper,
  MobileTop,
  MobileSide,
  MobileBottom,
  FacebookEditorWrapper,
  FacebookEditor,
  Header,
  EditorWrapper,
  PhotoEditorWrapper,
  FacebookInfoLogo,
  FooterWrapper,
  FooterTextSections,
  FACEBOOK_IMAGE_SIZE_SPEC,
  teaserTextStyle,
  displayLinkStyle,
  subHeadlineStyle,
  titleStyle
} from "./PostEditorFacebook.styled";
import PageHeader from "./page-header/PageHeader";
import useMediaAssets from "../hooks/useMediaAssets";
import TemplatePostPreview from "../../TemplatePost/TemplatePost__Preview/TemplatePostPreview";
import { TemplatePostStore, withRefluxStore } from "../../../../../Stores";
import { VERTICAL_TEMPLATE_IDS } from "../../constants.js";
import { useTranslation } from "react-i18next";

const PostEditorFacebook = ({
  orgaId,
  postData,
  channelData,
  updatePost,
  updateChannelData,
  mobile,
  adsetId,
  editable,
  channel,
  loading,
  clientView,
  selectedCreativeIndexToEdit,
  setSelectedCreativeIndexToEdit,
  imageDimensions,
  hasAts,
  campaignLanguage,
  // Store
  template,
  inputProps
}) => {
  logger.debug("PostEditorFacebook:render", "render:", {
    postData,
    channelData,
    mobile,
    editable,
    adsetId,
    imageDimensions,
    hasAts,
    campaignLanguage
  });

  const { t } = useTranslation();
  const mediaAssets = useMediaAssets(postData);

  const isPageEditable = useMemo(
    // Facebook pages should not be editable for end clients
    () => (editable ? !(hasAts && clientView) : !clientView),
    [editable, clientView, hasAts]
  );

  const multiTemplatesOrientation =
    template?.orientation === "multi" && inputProps?.orientation === "vertical";

  const verticalTemplate =
    VERTICAL_TEMPLATE_IDS.includes(template?.id) || multiTemplatesOrientation;

  const templateSC = verticalTemplate
    ? VerticalSC.VERTICAL_IMAGE_SPEC["facebook"][mobile ? "mobile" : "web"]
    : FACEBOOK_IMAGE_SIZE_SPEC[mobile ? "mobile" : "web"];

  const minAspect = verticalTemplate
    ? templateSC.minAspect
    : channelsByShortName.facebook.minAspect;

  const maxAspect = verticalTemplate
    ? templateSC.maxAspect
    : channelsByShortName.facebook.maxAspect;

  const templatePreviewSizes = {
    minHeight: templateSC.minHeight,
    maxHeight: templateSC.maxHeight,
    width: templateSC.pictureWidth,
    minAspect: minAspect,
    maxAspect: maxAspect
  };

  return (
    <PostEditorFacebookWrapper mobile={mobile} verticalTemplate={verticalTemplate}>
      {mobile && <MobileTop src="/images/mobile-top.png" alt="" />}
      <div style={{ display: "flex" }}>
        {mobile && <MobileSide src="/images/mobile-left.png" alt="" />}
        <FacebookEditorWrapper mobile={mobile} verticalTemplate={verticalTemplate}>
          <FacebookEditor mobile={mobile} verticalTemplate={verticalTemplate}>
            <Header>
              <CampaignCreatePageSelectorClient
                updateChannelData={updateChannelData}
                pageId={channelData?.pageId}
                adsetId={adsetId}
                editable={isPageEditable}
              >
                <PageHeader
                  loading={loading}
                  pageLogo={channelData?.pageLogo}
                  pageName={channelData?.pageName}
                  pageId={channelData?.pageId}
                  campaignLanguage={campaignLanguage}
                  sponsoredLogo
                />
              </CampaignCreatePageSelectorClient>
            </Header>
            <EditorWrapper>
              <CampaignCreateTeaserEditor
                channel="facebook"
                updatePost={updatePost}
                teaserText={postData.teaserText}
                editable={editable}
                teaserTextStyle={teaserTextStyle}
                smallButtons={mobile}
                isAutoGenerated={postData?.isAutoGenerated}
                autoGeneratedText={postData?.autoGeneratedText}
                isEdited={postData?.editedStates?.teaserText}
              />
            </EditorWrapper>
            <PhotoEditorWrapper>
              <TemplatePostPreview
                orgaId={orgaId}
                creatives={postData.creatives}
                creativeAssets={postData.creativeAssets}
                selectedMediaAssetIndexToEdit={selectedCreativeIndexToEdit}
                mediaAssets={mediaAssets}
                updatePost={updatePost}
                channel={channel}
                editable={editable}
                templatePreviewSizes={templatePreviewSizes}
              />

              <FacebookInfoLogo
                src="/images/facebook_info_icon.svg"
                alt=""
                data-tip=""
                data-for="facebook-sponsored-picture"
              />
              <Tooltip
                id="facebook-sponsored-picture"
                effect="solid"
                place="top"
                backgroundColor="#1C1928"
                offset={{ top: -5 }}
              >
                <span>{t("Facebook display image")}</span>
              </Tooltip>
            </PhotoEditorWrapper>
            <FooterWrapper>
              <FooterTextSections>
                <ChannelDataEditor
                  tooltip={t("Displayed link")}
                  updateChannelData={updateChannelData}
                  value={channelData.displayLink}
                  dataKey="displayLink"
                  textStyle={displayLinkStyle}
                  limit={channelsByShortName.facebook.limits.displayLink}
                  label={t("Displayed link")}
                  bannedCharaters={["!", "?"]}
                  editable={editable}
                  urlValue={channelData.url}
                />

                <ChannelDataEditor
                  tooltip={t("Job Title")}
                  updateChannelData={updateChannelData}
                  value={channelData.title}
                  dataKey="title"
                  textStyle={titleStyle}
                  limit={channelsByShortName.facebook.limits.title}
                  label={t("Job Title")}
                  editable={editable}
                />

                <ChannelDataEditor
                  tooltip={t("Subtitle")}
                  updateChannelData={updateChannelData}
                  value={channelData.subHeadline}
                  dataKey="subHeadline"
                  textStyle={subHeadlineStyle}
                  limit={channelsByShortName.facebook.limits.subHeadline}
                  label={t("Subtitle")}
                  editable={editable}
                />
              </FooterTextSections>
              <ButtonOutlinedAnchor target="_blank" href={channelData?.url || "#"}>
                {t("Read more", { lng: campaignLanguage })}
              </ButtonOutlinedAnchor>
            </FooterWrapper>
            {!loading && (
              <CampaignCreatePerformanceEditor
                creatives={postData.creatives}
                creativeAssets={postData.creativeAssets}
                logos={postData.logos || new Array(postData.creatives.length).fill("")}
                editable={editable}
                channel={channel}
                mediaAssets={mediaAssets}
                updatePost={updatePost}
                padding="0 11px 16px"
                selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
                setSelectedCreativeIndexToEdit={setSelectedCreativeIndexToEdit}
              />
            )}
          </FacebookEditor>
        </FacebookEditorWrapper>
        {mobile && <MobileSide src="/images/mobile-right.png" alt="" />}
      </div>
      {mobile && <MobileBottom src="/images/mobile-bottom.png" alt="" />}
    </PostEditorFacebookWrapper>
  );
};

export default withRefluxStore(PostEditorFacebook, [TemplatePostStore], ["template", "inputProps"]);
